import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { finalize } from 'rxjs';

import {
  ExcludedAttachmentItem,
  PrintAttachmentValidationResult,
  QueueEmailPendingPayload,
  QueueFilePendingPayload,
  QueueJobStatus,
  QueueJobType,
} from '@models/api';
import { KeyValueIsPrimary } from '@models/common';
import { RouteCodeEnum } from '@models/menu';
import { ModuleInstanceService } from '@module-instance/services';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TemplateTypeEnum } from '@shared/consts';
import { NotificationService, QueueNotificationService } from '@shared/services';
import { DictionaryStore, UserStore } from '@signal-store/index';

export interface ActionDialogData {
  contractId: number;
  contractNo: string;
  moduleId: number;
  moduleName: string;
  moduleCode: RouteCodeEnum;
  entityInstances: EntityInstance[];
  actionType: 'download' | 'print' | 'email';
  isContractClosedOrArchived?: boolean;
}

export interface EntityInstance {
  entityInstanceId: number;
  documentNumber?: string;
  fileName?: string;
  isClosedState?: boolean;
  screenId: number;
}

export type ExcludedAttachmentsByDocument = {
  documentNumber: string;
  items: ExcludedAttachmentItem[];
};

const LIMIT_SIZE_BYTES = 104_857_600; // 100 MB

@Component({
  standalone: true,
  imports: [
    FlexLayoutModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    NgxSkeletonLoaderModule,
    FormsModule,
    TranslateModule,
  ],
  templateUrl: './actions-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionsDialogComponent implements OnInit, OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<ActionsDialogComponent>);
  private readonly userStore = inject(UserStore);
  private readonly dictionaryStore = inject(DictionaryStore);
  private readonly moduleInstanceService = inject(ModuleInstanceService);
  private readonly notify = inject(NotificationService);
  private readonly queueNotificationService = inject(QueueNotificationService);
  readonly data = inject<ActionDialogData>(MAT_DIALOG_DATA);

  templates = signal<KeyValueIsPrimary<number>[]>([]);
  showTemplates = signal(true);
  isTemplatesLoading = signal(true);
  selectedTemplateId = signal<number>(null);

  actionType = signal<'download' | 'print' | 'email'>(this.data.actionType);
  isPrint = computed(() => this.actionType() === 'print');
  isEmail = computed(() => this.actionType() === 'email');
  isDownload = computed(() => this.actionType() === 'download');
  jobType = computed(() =>
    this.isEmail() ? QueueJobType.Email : this.isPrint() ? QueueJobType.Print : QueueJobType.Download,
  );
  preparingLabelKey = computed(() =>
    this.isEmail() ? 'queue.email.preparing' : this.isPrint() ? 'queue.print.preparing' : 'queue.download.preparing',
  );

  isBundleDocuments = signal(this.data.entityInstances.length >= 2);
  isIncludeActivity = signal(false);
  isIncludeDocumentAttachments = signal(false);
  isIncludeActivityAttachments = signal(false);

  isLoading = signal(false);
  printValidationResult = signal<PrintAttachmentValidationResult>(null);
  showUnsupportedReview = signal(false);
  printWarningBanner = signal<'sizeLimit' | 'unsupported' | null>(null);

  excludedAttachmentsGrouped = computed<ExcludedAttachmentsByDocument[]>(() => {
    const validation = this.printValidationResult();
    if (!validation?.unsupportedAttachments?.length) {
      return [];
    }

    const groupedMap = new Map<string, ExcludedAttachmentItem[]>();
    for (const item of validation.unsupportedAttachments) {
      const key = item.documentNumber || `#${item.entityInstanceId}`;
      const existing = groupedMap.get(key) ?? [];
      existing.push(item);
      groupedMap.set(key, existing);
    }

    return Array.from(groupedMap.entries()).map(([documentNumber, items]) => ({
      documentNumber,
      items,
    }));
  });

  private hasQueuedOperationInFlight = false;

  ngOnInit(): void {
    this.init();
  }

  onOptionChange(setter: (value: boolean) => void, checked: boolean) {
    setter(checked);
    this.resetPrintWarnings();
  }

  onConfirm() {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.hasQueuedOperationInFlight = true;

    const body = this.getRequestBody();
    const title = this.getFileName();

    if (this.isEmail()) {
      this.handleEmailAction(body, title);
    } else if (this.isPrint()) {
      this.handlePrintWithValidation(title);
    } else {
      this.handleDownloadAction(body, title);
    }
  }

  onContinuePrintAnyway() {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.hasQueuedOperationInFlight = true;
    this.printWarningBanner.set(null);
    this.showUnsupportedReview.set(false);
    this.executeDirectPrint(this.getFileName());
  }

  onToggleUnsupportedReview() {
    this.showUnsupportedReview.update(v => !v);
  }

  onSwitchToDownload() {
    this.actionType.set('download');
    this.printWarningBanner.set(null);
    this.showUnsupportedReview.set(false);
    this.printValidationResult.set(null);
  }

  getReasonTranslationKey(reason: string): string {
    switch (reason) {
      case 'missingExtension':
        return 'pdfHandler.unsupportedReasonMissingExtension';
      case 'emptyFile':
        return 'pdfHandler.unsupportedReasonEmptyFile';
      default:
        return 'pdfHandler.unsupportedReasonFormat';
    }
  }

  ngOnDestroy(): void {
    if (this.hasQueuedOperationInFlight) {
      this.hasQueuedOperationInFlight = false;
      this.queueNotificationService.notifyContinueInBackground(this.jobType());
    }
  }

  private init() {
    // Legacy: if multiple screens, check if they are the same.
    const uniqueScreenIds = [...new Set(this.data.entityInstances.map(x => x.screenId).filter(x => x))];
    const screenId = uniqueScreenIds.length === 1 ? uniqueScreenIds[0] : null;

    const allClosed = this.data.entityInstances.every(x => x.isClosedState);
    if (allClosed || this.data.isContractClosedOrArchived) {
      this.showTemplates.set(false);
    }

    this.isTemplatesLoading.set(true);
    this.dictionaryStore
      .getUserTemplates({
        contractId: this.data.contractId,
        moduleId: this.data.moduleId,
        screenId,
        typeName: TemplateTypeEnum.Printout,
      })
      .pipe(finalize(() => this.isTemplatesLoading.set(false)))
      .subscribe(templates => {
        if (!templates?.length) {
          this.notify.warning('pdfHandler.noTemplatesFound');
          this.dialogRef.close();
          return;
        }

        const defaultTemplate = templates.find(t => t.isPrimary) || templates[0];

        // If multiple screens, restrict to default template only
        this.templates.set(uniqueScreenIds.length > 1 ? [defaultTemplate] : templates);

        this.selectedTemplateId.set(defaultTemplate.key);
      });
  }

  private resetPrintWarnings() {
    if (this.printWarningBanner() === 'unsupported' || this.printWarningBanner() === 'sizeLimit') {
      this.printWarningBanner.set(null);
      this.printValidationResult.set(null);
      this.showUnsupportedReview.set(false);
    }
  }

  private handlePrintWithValidation(title: string) {
    const hasAttachments = this.isIncludeDocumentAttachments() || this.isIncludeActivityAttachments();
    if (!hasAttachments && this.data.entityInstances.length * 250_000 <= LIMIT_SIZE_BYTES) {
      this.executeDirectPrint(title);
      return;
    }

    this.moduleInstanceService
      .validatePrintAttachments({
        contractId: this.data.contractId,
        moduleId: this.data.moduleId,
        body: this.data.entityInstances.map(x => x.entityInstanceId),
        isIncludeActivity: this.isIncludeActivity(),
        isIncludeDocumentAttachments: this.isIncludeDocumentAttachments(),
        isIncludeActivityAttachments: this.isIncludeActivityAttachments(),
      })
      .subscribe({
        next: validation => {
          this.printValidationResult.set(validation);

          if (validation.exceedsSizeLimit || validation.totalSizeBytes > LIMIT_SIZE_BYTES) {
            this.resetInFlightState();
            this.printWarningBanner.set('sizeLimit');
            this.actionType.set('download');
            return;
          }

          if (validation.unsupportedCount > 0) {
            this.resetInFlightState();
            this.printWarningBanner.set('unsupported');
            return;
          }

          this.executeDirectPrint(title);
        },
        error: () => this.resetInFlightState(),
      });
  }

  private executeDirectPrint(title: string) {
    const body = {
      list: this.data.entityInstances.map(instance => instance.entityInstanceId),
      isPrint: true,
    };
    this.isLoading.set(true);
    this.hasQueuedOperationInFlight = true;

    this.moduleInstanceService
      .getInstancesAsPdf({
        contractId: this.data.contractId,
        moduleId: this.data.moduleId,
        body,
        title,
        templateId: this.selectedTemplateId() || undefined,
        isIncludeActivity: this.isIncludeActivity(),
        isIncludeDocumentAttachments: this.isIncludeDocumentAttachments(),
        isIncludeActivityAttachments: this.isIncludeActivityAttachments(),
        isBundleDocuments: true,
        jobType: QueueJobType.Print,
      })
      .subscribe({
        next: response => {
          if (!response?.operationId) {
            this.resetInFlightState();
            return;
          }

          this.queueNotificationService.registerOperation<QueueFilePendingPayload>({
            operationId: response.operationId,
            jobId: response.jobId,
            jobType: QueueJobType.Print,
            payload: {
              fileName: title,
            },
            onSettled: status => {
              this.resetInFlightState();
              if (status === QueueJobStatus.Completed) {
                this.dialogRef.close(true);
              }
            },
          });
        },
        error: () => this.resetInFlightState(),
      });
  }

  private handleDownloadAction(body: { list: number[]; isPrint: boolean }, title: string) {
    const hasAttachments = this.isIncludeDocumentAttachments() || this.isIncludeActivityAttachments();
    if (!hasAttachments && this.data.entityInstances.length * 250_000 <= LIMIT_SIZE_BYTES) {
      this.handleDirectDownload(body, title);
      return;
    }

    this.moduleInstanceService
      .getAttachmentsSize({
        contractId: this.data.contractId,
        moduleId: this.data.moduleId,
        body: this.data.entityInstances.map(x => x.entityInstanceId),
        isIncludeActivity: this.isIncludeActivity(),
        isIncludeDocumentAttachments: this.isIncludeDocumentAttachments(),
        isIncludeActivityAttachments: this.isIncludeActivityAttachments(),
      })
      .subscribe({
        next: size => {
          if (size > LIMIT_SIZE_BYTES) {
            this.moduleInstanceService
              .backgroundDownloadInstances({
                contractId: this.data.contractId,
                moduleId: this.data.moduleId,
                body: this.data.entityInstances.map(x => x.entityInstanceId),
                title,
                templateId: this.selectedTemplateId() || undefined,
                isIncludeActivity: this.isIncludeActivity(),
                isIncludeDocumentAttachments: this.isIncludeDocumentAttachments(),
                isIncludeActivityAttachments: this.isIncludeActivityAttachments(),
                isBundleDocuments: this.data.entityInstances.length > 1 ? this.isBundleDocuments() : true,
              })
              .subscribe({
                next: () => {
                  this.hasQueuedOperationInFlight = false;
                  this.isLoading.set(false);
                  this.notify.warning('pdfHandler.downloadSizeLimit');
                  this.dialogRef.close(true);
                },
                error: () => this.resetInFlightState(),
              });
          } else {
            this.handleDirectDownload(body, title);
          }
        },
        error: () => this.resetInFlightState(),
      });
  }

  private handleEmailAction(body: { list: number[]; isPrint: boolean }, title: string) {
    const isMultiInstance = this.data.entityInstances.length > 1;
    this.moduleInstanceService
      .getInstancesAsPdf({
        contractId: this.data.contractId,
        moduleId: this.data.moduleId,
        body,
        title,
        templateId: this.selectedTemplateId() || undefined,
        isIncludeActivity: this.isIncludeActivity(),
        isIncludeDocumentAttachments: this.isIncludeDocumentAttachments(),
        isIncludeActivityAttachments: this.isIncludeActivityAttachments(),
        isBundleDocuments: isMultiInstance ? this.isBundleDocuments() : true,
        jobType: QueueJobType.Email,
      })
      .subscribe({
        next: response => {
          if (!response?.operationId) {
            this.resetInFlightState();
            return;
          }

          this.queueNotificationService.registerOperation<QueueEmailPendingPayload>({
            operationId: response.operationId,
            jobId: response.jobId,
            jobType: QueueJobType.Email,
            payload: {
              fileName: title,
              contractId: this.data.contractId,
              contractNo: this.data.contractNo,
              moduleId: this.data.moduleId,
              moduleName: this.data.moduleName,
              moduleCode: this.data.moduleCode,
              entityInstanceIds: this.data.entityInstances.map(x => x.entityInstanceId),
            },
            onSettled: status => {
              this.resetInFlightState();
              if (status === QueueJobStatus.Completed) {
                this.dialogRef.close(false);
              }
            },
          });
        },
        error: () => this.resetInFlightState(),
      });
  }

  private handleDirectDownload(body: { list: number[]; isPrint: boolean }, title: string) {
    const isMultiInstance = this.data.entityInstances.length > 1;
    const jobType = this.isPrint() ? QueueJobType.Print : QueueJobType.Download;

    this.moduleInstanceService
      .getInstancesAsPdf({
        contractId: this.data.contractId,
        moduleId: this.data.moduleId,
        body,
        title,
        templateId: this.selectedTemplateId() || undefined,
        isIncludeActivity: this.isIncludeActivity(),
        isIncludeDocumentAttachments: this.isIncludeDocumentAttachments(),
        isIncludeActivityAttachments: this.isIncludeActivityAttachments(),
        isBundleDocuments: this.isPrint() ? true : isMultiInstance ? this.isBundleDocuments() : true,
        jobType,
      })
      .subscribe({
        next: response => {
          if (!response?.operationId) {
            this.resetInFlightState();
            return;
          }

          this.queueNotificationService.registerOperation<QueueFilePendingPayload>({
            operationId: response.operationId,
            jobId: response.jobId,
            jobType,
            payload: {
              fileName: title,
            },
            onSettled: status => {
              this.resetInFlightState();
              if (status === QueueJobStatus.Completed) {
                this.dialogRef.close(true);
              }
            },
          });
        },
        error: () => this.resetInFlightState(),
      });
  }

  private resetInFlightState(): void {
    this.hasQueuedOperationInFlight = false;
    this.isLoading.set(false);
  }

  private getRequestBody(): { list: number[]; isPrint: boolean } {
    return {
      list: this.data.entityInstances.map(instance => instance.entityInstanceId),
      isPrint: this.isPrint(),
    };
  }

  private getFileName(instance?: EntityInstance, ext: string = '.pdf') {
    let fileName = instance?.fileName;

    if (!fileName) {
      const documentNumber =
        instance?.documentNumber ||
        (this.data.entityInstances.length === 1 ? this.data.entityInstances[0].documentNumber : null);

      const moduleValue = documentNumber ? this.data.moduleName : this.data.moduleCode;

      const contractNo = this.data.moduleCode === RouteCodeEnum.Contracts ? null : this.data.contractNo;

      fileName = [moduleValue, documentNumber, contractNo].filter(x => x).join('-');

      if (!fileName) fileName = 'Documents';

      const timezone = this.userStore.userData()?.ianaTimeZoneId;
      const timestamp = moment.tz(new Date(), timezone).format('YYYY_MM_DD_HH_mm_ss');

      fileName += ` - ${timestamp}`;
    }

    return (fileName + ext).replace(/[<>:"\/\\|?*]+/g, '_');
  }
}
